import * as NextSkyTheme from "@NextSkyTheme/global";

/*
 * Free gift with purchase.
 *
 * Tiers are configured in global theme settings and rendered into
 * <free-gift-progress-bar data-tiers="..."> by snippets/free-gift-progress-bar.liquid.
 * <free-gift-ping> lives inside the cart containers that are innerHTML-swapped on
 * every cart mutation (.cart-drawer__form / .main-cart-items), so its
 * connected/disconnectedCallback fires on every cart change — including the
 * quantity/remove paths that never publish the cartUpdate event.
 *
 * Gift mutations request `sections` in the POST body (exactly like the theme's
 * own add-to-cart and quantity updates) and re-render through the theme's own
 * updateCartDrawer/updateMainCart methods, so the added/removed gift line is
 * immediately visible in the cart drawer and on the cart page.
 *
 * Pricing is guaranteed by automatic Buy X Get Y discounts in Shopify admin;
 * this module only adds/removes the gift lines and paints the savings bar.
 */

const STORAGE_PREFIX = "fg_added_";

function storageGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function storageSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    /* private mode */
  }
}

function storageRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    /* private mode */
  }
}

const FreeGiftManager = {
  tiers: null,
  unavailable: new Set(),
  inFlight: false,
  pending: false,
  timer: null,

  // The theme's own cart-items element: owns getSectionsToRender() and the
  // updateCartDrawer/updateMainCart render methods (inherited from ProductForm).
  get itemsElement() {
    return (
      document.querySelector("main-cart-items") ||
      document.querySelector("cart-items")
    );
  },

  get rate() {
    const rate = Number(
      window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
    );
    return rate > 0 ? rate : 1;
  },

  thresholdCents(tier) {
    return Math.round(tier.threshold * this.rate * 100);
  },

  readTiers() {
    const bar = document.querySelector("free-gift-progress-bar");
    if (!bar || !bar.dataset.tiers) return null;
    try {
      return JSON.parse(bar.dataset.tiers)
        .filter((tier) => tier.threshold > 0 && tier.variantId)
        .sort((a, b) => a.threshold - b.threshold);
    } catch (e) {
      return null;
    }
  },

  sectionIds() {
    const itemsElement = this.itemsElement;
    if (!itemsElement || typeof itemsElement.getSectionsToRender !== "function") {
      return null;
    }
    return [
      ...new Set(itemsElement.getSectionsToRender().map((s) => s.section)),
    ];
  },

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.evaluate(), 150);
  },

  async evaluate() {
    this.tiers = this.readTiers();
    if (!this.tiers || !this.tiers.length) return;
    if (this.inFlight) {
      this.pending = true;
      return;
    }
    this.inFlight = true;
    try {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      const cart = await response.json();
      const giftLines = cart.items.filter(
        (item) => item.properties && item.properties._free_gift
      );
      const subtotal = cart.items.reduce(
        (sum, item) =>
          item.properties && item.properties._free_gift
            ? sum
            : sum + item.final_line_price,
        0
      );
      this.paint(subtotal, cart.total_price);

      // Dropping below a tier clears its dismissal flag: a fresh crossing is a fresh offer.
      this.tiers.forEach((tier) => {
        if (subtotal < this.thresholdCents(tier)) {
          storageRemove(STORAGE_PREFIX + tier.variantId);
        }
      });

      const toAdd = this.tiers.filter(
        (tier) =>
          subtotal >= this.thresholdCents(tier) &&
          tier.available !== false &&
          !this.unavailable.has(tier.variantId) &&
          !giftLines.some((line) => line.variant_id === tier.variantId) &&
          !storageGet(STORAGE_PREFIX + tier.variantId)
      );
      const toRemove = giftLines.filter((line) => {
        const tier = this.tiers.find((t) => t.variantId === line.variant_id);
        return !tier || subtotal < this.thresholdCents(tier);
      });
      const toFix = giftLines.filter(
        (line) => line.quantity > 1 && !toRemove.includes(line)
      );

      let lastState = null;
      if (toAdd.length) {
        lastState = (await this.addGifts(toAdd)) || lastState;
      }
      for (const line of toRemove) {
        lastState = (await this.changeLine(line.key, 0)) || lastState;
        storageRemove(STORAGE_PREFIX + line.variant_id);
      }
      for (const line of toFix) {
        lastState = (await this.changeLine(line.key, 1)) || lastState;
      }
      if (lastState) {
        this.renderFromState(lastState);
      }
    } catch (e) {
      /* network hiccup — the next cart mutation re-triggers evaluation */
    } finally {
      this.inFlight = false;
      if (this.pending) {
        this.pending = false;
        this.schedule();
      }
    }
  },

  async addGifts(tiers) {
    const state = await this.postAdd(tiers);
    if (state) return state;
    if (tiers.length === 1) {
      this.unavailable.add(tiers[0].variantId);
      return null;
    }
    // Batch failed (e.g. one variant out of stock) — retry individually.
    let lastState = null;
    for (const tier of tiers) {
      const tierState = await this.postAdd([tier]);
      if (tierState) lastState = tierState;
      else this.unavailable.add(tier.variantId);
    }
    return lastState;
  },

  async postAdd(tiers) {
    const addUrl =
      (window.routes && window.routes.cart_add_url) || "/cart/add.js";
    const body = {
      items: tiers.map((tier) => ({
        id: tier.variantId,
        quantity: 1,
        properties: { _free_gift: "1" },
      })),
    };
    const sections = this.sectionIds();
    if (sections) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(addUrl, {
      ...NextSkyTheme.fetchConfig(),
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    tiers.forEach((tier) => storageSet(STORAGE_PREFIX + tier.variantId, "1"));
    return response.json();
  },

  async changeLine(key, quantity) {
    const changeUrl =
      (window.routes && window.routes.cart_change_url) || "/cart/change.js";
    const body = { id: key, quantity };
    const sections = this.sectionIds();
    if (sections) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(changeUrl, {
      ...NextSkyTheme.fetchConfig(),
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    return response.json();
  },

  // Re-render the cart surfaces through the theme's own battle-tested methods.
  renderFromState(parsedState) {
    if (!parsedState || !parsedState.sections) return;
    const itemsElement = this.itemsElement;
    if (!itemsElement) return;
    try {
      if (
        document.querySelector("main-cart") &&
        typeof itemsElement.updateMainCart === "function"
      ) {
        itemsElement.updateMainCart(parsedState);
      } else if (
        document.querySelector("cart-drawer") &&
        typeof itemsElement.updateCartDrawer === "function"
      ) {
        itemsElement.updateCartDrawer(parsedState);
      }
    } catch (e) {
      /* render hiccup: cart state is still correct; next mutation re-renders */
    }
    new NextSkyTheme.FSProgressBar("free-ship-progress-bar");
  },

  paint(subtotal, total) {
    document
      .querySelectorAll("free-gift-progress-bar")
      .forEach((bar) => bar.paint(subtotal, this.unavailable, total));
  },
};

class FreeGiftPing extends HTMLElement {
  connectedCallback() {
    FreeGiftManager.schedule();
  }

  disconnectedCallback() {
    FreeGiftManager.schedule();
  }
}

class FreeGiftProgressBar extends HTMLElement {
  connectedCallback() {
    try {
      this.tiers = JSON.parse(this.dataset.tiers || "[]")
        .filter((tier) => tier.threshold > 0 && tier.variantId)
        .sort((a, b) => a.threshold - b.threshold);
    } catch (e) {
      this.tiers = [];
    }
    const initial = Number(this.dataset.subtotal);
    if (!Number.isNaN(initial)) {
      this.paint(initial, FreeGiftManager.unavailable);
    }
  }

  paint(subtotal, unavailable = new Set(), total) {
    if (!this.tiers || !this.tiers.length) return;
    const rate = FreeGiftManager.rate;
    // One bar at a time: stay hidden until the free-shipping threshold is
    // reached (the CSS :has() rule hides the sibling free-shipping bar once
    // this bar is visible). data-fs-min is only set when the surface shows
    // the free-shipping bar.
    if (typeof total === "number") {
      const fsMin = Number(this.dataset.fsMin) || 0;
      if (fsMin > 0) {
        this.classList.toggle(
          "fg-hidden",
          total < Math.round(fsMin * rate * 100)
        );
      }
    }
    const cents = (tier) => Math.round(tier.threshold * rate * 100);
    const maxCents = cents(this.tiers[this.tiers.length - 1]);
    const fill = this.querySelector(".fg-bar__fill");
    if (fill) {
      fill.style.setProperty(
        "--width",
        Math.min(100, (subtotal / maxCents) * 100) + "%"
      );
    }

    const unreached = this.tiers.filter((tier) => subtotal < cents(tier));
    const next =
      unreached.find(
        (tier) => tier.available !== false && !unavailable.has(tier.variantId)
      ) || unreached[0];

    this.querySelectorAll("[data-fg-milestone]").forEach((milestone) => {
      const variantId = Number(milestone.dataset.variantId);
      const thresholdCents = Math.round(
        Number(milestone.dataset.threshold) * rate * 100
      );
      milestone.classList.toggle("is-reached", subtotal >= thresholdCents);
      milestone.classList.toggle(
        "is-soldout",
        unavailable.has(variantId) || milestone.dataset.available === "false"
      );
      milestone.classList.toggle(
        "is-next",
        Boolean(next) && variantId === next.variantId
      );
    });

    const messageElement = this.querySelector(".fg-bar__message");
    if (!messageElement) return;
    if (!next) {
      this.classList.add("is-complete");
      messageElement.innerHTML = this.dataset.messageComplete || "";
    } else {
      this.classList.remove("is-complete");
      const remaining = cents(next) - subtotal;
      const template = (this.dataset.message || "").replace(
        "[amount]",
        '<span class="fg-bar__price">[amount]</span>'
      );
      messageElement.innerHTML = template
        .replace(
          "[amount]",
          NextSkyTheme.formatMoney(
            remaining,
            window.themeGlobalVariables &&
              window.themeGlobalVariables.settings.money_format
          )
        )
        .replace("[product]", next.title || "");
    }
  }
}

if (!customElements.get("free-gift-ping")) {
  customElements.define("free-gift-ping", FreeGiftPing);
}
if (!customElements.get("free-gift-progress-bar")) {
  customElements.define("free-gift-progress-bar", FreeGiftProgressBar);
}

FreeGiftManager.schedule();
document.addEventListener("shopify:section:load", () =>
  FreeGiftManager.schedule()
);
